import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import type React from 'react';
import styled from 'styled-components';
import { SaveBanner } from '@/saveBanner';
import { LeftMenu } from '~/components/navigation/LeftMenu';
import { StudentFocusProvider, useStudentFocus } from '~/modules/studentFocus';

export const Route = createFileRoute('/_admin')({
  component: RouteComponent,
});

const RootContainer = styled.div`
  display: grid;
  grid-template-columns: var(--navigation-width) 1fr;
  grid-template-rows: auto 1fr;
  min-height: 100vh;
  `;

const LeftMenuContainer = styled.div`
  grid-row: 1 / -1;
  background: var(--grey-25);
  padding-top: var(--top-nav-height);
  `;

// Room below the last row, so long lists don't end flush with the window.
const BodyContainer = styled.div`
  min-width: 0;
  padding-bottom: 5rem;
  padding-left: var(--body-padding-left);
  padding-right: var(--body-padding-left);
  padding-top: var(--body-padding-top);
`;

const FocusBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0.875rem;
  margin-bottom: var(--component-margin);
  background: color-mix(in srgb, var(--orange) 12%, transparent);
  border-left: 3px solid var(--orange);
  border-radius: 4px;
  font-size: 0.875rem;
`;

const FocusBannerText = styled.span`
  color: var(--grey-900);

  strong {
    font-weight: 600;
  }
`;

const FocusBannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const focusStudentLinkStyle: React.CSSProperties = {
  color: 'var(--blue)',
  textDecoration: 'none',
};

const ExitFocusButton = styled.button`
  background: none;
  border: 1px solid var(--orange);
  border-radius: 4px;
  color: var(--grey-900);
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;

  &:hover {
    background: color-mix(in srgb, var(--orange) 15%, transparent);
  }
`;

const NAVIGATION_MENU = [
  {
    text: 'Students',
    to: '/students',
  },
  {
    text: 'Campaigns',
    to: '/campaigns',
  },
  {
    text: 'Series',
    to: '/series',
  },
  {
    text: 'Courses',
    to: '/courses',
  },
  {
    text: 'Lessons',
    to: '/lessons',
  },
  {
    text: 'Topics',
    to: '/topics',
  },
  {
    text: 'Questions',
    to: '/questions',
  },
  {
    text: 'Applications',
    to: '/applications',
  },
  {
    text: 'Groups',
    to: '/groups',
  },
  {
    text: 'Study Helpers',
    to: '/study-helpers',
  },
  {
    text: 'Quiz Submissions',
    to: '/quiz-submissions',
  },
  {
    text: 'Feedbacks',
    to: '/feedbacks',
  },
  {
    text: 'Certificates',
    to: '/certificates',
  },
  {
    text: 'Onboardings',
    to: '/onboardings',
  },
];

function StudentFocusBanner() {
  const { focusedStudent, exitFocus } = useStudentFocus();
  const navigate = useNavigate();

  if (!focusedStudent) return null;

  const goToStudent = () =>
    navigate({
      to: '/students/$studentId',
      params: { studentId: String(focusedStudent.id) },
    });

  return (
    <FocusBanner>
      <FocusBannerText>
        Viewing data for{' '}
        <button
          type="button"
          onClick={goToStudent}
          style={{
            ...focusStudentLinkStyle,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit',
          }}
        >
          <strong>{focusedStudent.name}</strong>
        </button>{' '}
        — all lists are filtered to this student
      </FocusBannerText>
      <FocusBannerActions>
        <button
          type="button"
          onClick={goToStudent}
          style={{
            ...focusStudentLinkStyle,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit',
            fontSize: '0.875rem',
          }}
        >
          View student
        </button>
        <ExitFocusButton type="button" onClick={exitFocus}>
          Exit focus
        </ExitFocusButton>
      </FocusBannerActions>
    </FocusBanner>
  );
}

function RouteComponent() {
  return (
    <StudentFocusProvider>
      <RootContainer>
        <LeftMenuContainer>
          <LeftMenu options={NAVIGATION_MENU} />
        </LeftMenuContainer>
        <BodyContainer>
          <SaveBanner />
          <StudentFocusBanner />
          <Outlet />
        </BodyContainer>
      </RootContainer>
    </StudentFocusProvider>
  );
}
